/**
 * BuchhaltungJournal — Buchungssätze: liste + manuel kayıt + Storno.
 * Soll=Haben canlı doğrulanır; kayıt asla silinmez (GoBD), Storno atılır.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Plus, Trash2, Undo2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/api/AuthContext';
import { listAccounts, listJournalEntries, postEntry, reverseEntry, fmtEUR } from '@/lib/buchhaltungApi';

const EMPTY_LINE = { account_no: '', debit: '', credit: '' };

export default function BuchhaltungJournal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);

  const { data: accounts = [] } = useQuery({ queryKey: ['fibu-accounts'], queryFn: () => listAccounts() });
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['fibu-journal'],
    queryFn: () => listJournalEntries({}),
  });

  const sumDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const sumCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = sumDebit > 0 && Math.abs(sumDebit - sumCredit) < 0.005;

  const filtered = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    return entries.filter((e) =>
      (e.entry_no || '').toLowerCase().includes(needle) ||
      (e.description || '').toLowerCase().includes(needle)
    );
  }, [entries, searchTerm]);

  const resetForm = () => {
    setDescription('');
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  };

  const postMutation = useMutation({
    mutationFn: () => postEntry({
      bookingDate,
      description,
      lines: lines
        .filter((l) => l.account_no && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0))
        .map((l) => ({
          account_no: l.account_no,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      actorEmail: user?.email,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries();
      toast.success('Buchung gespeichert.');
      resetForm();
      setShowForm(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const stornoMutation = useMutation({
    mutationFn: (id) => reverseEntry(id, user?.email),
    onSuccess: () => {
      queryClient.invalidateQueries();
      toast.success('Storno gebucht — Originalbuchung bleibt erhalten.');
    },
    onError: (err) => toast.error(err.message),
  });

  const updateLine = (idx, field, value) => {
    const next = [...lines];
    next[idx] = { ...next[idx], [field]: value };
    setLines(next);
  };

  return (
    <PageShell
      head={(
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <PageHeader icon={<BookOpen className="w-5 h-5" />} title="Journal" />
            <Badge variant="outline" className="text-[10px]">{entries.length} Buchungen</Badge>
          </div>
          <Button onClick={() => setShowForm(true)} className="h-11 bg-slate-900 hover:bg-slate-800 gap-1.5 text-xs font-bold">
            <Plus className="w-4 h-4" /> Buchung
          </Button>
        </div>
      )}
    >

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Nach Nr. oder Text suchen..."
          className="pl-10 h-11 bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        {isLoading && <p className="text-center text-slate-400 py-8 text-sm">Lade Journal…</p>}
        {!isLoading && filtered.length === 0 && (
          <Card className="border-2 border-dashed">
            <CardContent className="p-10 text-center text-slate-400 text-sm">
              Noch keine Buchungen. Rechnungen können im Finanzen-Dashboard übernommen werden.
            </CardContent>
          </Card>
        )}
        {filtered.map((entry) => {
          const entryDebit = (entry.fibu_journal_lines || []).reduce((s, l) => s + Number(l.debit), 0);
          return (
            <Card key={entry.id} className={cn(entry.status === 'reversed' && 'opacity-60')}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-xs font-bold text-slate-700">{entry.entry_no}</span>
                      <Badge variant="outline" className="text-[9px] h-4 px-1.5">{entry.source}</Badge>
                      {entry.status === 'reversed' && (
                        <Badge className="text-[9px] h-4 px-1.5 bg-red-100 text-red-700 border-none">Storniert</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-800 mt-0.5 truncate">{entry.description || '—'}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(entry.booking_date).toLocaleDateString('de-DE')} · P{entry.period}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <p className="text-sm font-black text-slate-900">{fmtEUR(entryDebit)}</p>
                    {entry.status !== 'reversed' && entry.source === 'manual' && !entry.source_id?.startsWith('reversal:') && (
                      <Button
                        size="icon" variant="ghost"
                        className="h-9 w-9 text-slate-400 hover:text-red-600"
                        aria-label="Buchung stornieren"
                        title="Stornieren (Gegenbuchung)"
                        disabled={stornoMutation.isPending}
                        onClick={() => {
                          if (window.confirm(`${entry.entry_no} stornieren? Es wird eine Gegenbuchung erzeugt.`)) {
                            stornoMutation.mutate(entry.id);
                          }
                        }}
                      >
                        <Undo2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Satırlar */}
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
                  {(entry.fibu_journal_lines || []).sort((a, b) => a.line_no - b.line_no).map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-[11px]">
                      <span className="font-mono text-slate-600 truncate">
                        {l.account_no} {accounts.find((a) => a.account_no === l.account_no)?.name || ''}
                      </span>
                      <span className="font-mono text-slate-800 flex-shrink-0 ml-2">
                        {Number(l.debit) > 0 ? `S ${fmtEUR(l.debit)}` : `H ${fmtEUR(l.credit)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Manuel Buchung formu */}
      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Manuelle Buchung</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 max-w-2xl mx-auto pb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Buchungsdatum</label>
                <Input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} className="h-11" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500">Buchungstext</label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="z.B. Miete Juli" className="h-11" />
              </div>
            </div>

            <div className="space-y-2">
              {lines.map((line, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Select value={line.account_no} onValueChange={(v) => updateLine(idx, 'account_no', v)}>
                    <SelectTrigger className="h-11 flex-1 text-xs">
                      <SelectValue placeholder="Konto wählen…" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.account_no} value={a.account_no} className="text-xs">
                          {a.account_no} · {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number" inputMode="decimal" placeholder="Soll"
                    value={line.debit}
                    onChange={(e) => updateLine(idx, 'debit', e.target.value)}
                    className="h-11 w-24 text-right text-xs"
                  />
                  <Input
                    type="number" inputMode="decimal" placeholder="Haben"
                    value={line.credit}
                    onChange={(e) => updateLine(idx, 'credit', e.target.value)}
                    className="h-11 w-24 text-right text-xs"
                  />
                  {lines.length > 2 && (
                    <Button size="icon" variant="ghost" aria-label="Zeile entfernen" className="h-9 w-9 text-red-400"
                      onClick={() => setLines(lines.filter((_, i) => i !== idx))}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" className="h-9 text-xs gap-1"
                onClick={() => setLines([...lines, { ...EMPTY_LINE }])}>
                <Plus className="w-3.5 h-3.5" /> Zeile
              </Button>
            </div>

            {/* Canlı Soll/Haben denge göstergesi */}
            <div className={cn(
              'p-3 rounded-lg text-xs font-bold flex items-center justify-between',
              balanced ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            )}>
              <span>Soll: {fmtEUR(sumDebit)}</span>
              <span>{balanced ? '✓ ausgeglichen' : 'Differenz: ' + fmtEUR(sumDebit - sumCredit)}</span>
              <span>Haben: {fmtEUR(sumCredit)}</span>
            </div>

            <Button
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 font-bold"
              disabled={!balanced || !description.trim() || postMutation.isPending}
              onClick={() => postMutation.mutate()}
            >
              {postMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buchen'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </PageShell>
  );
}
